import memoizeOne from 'memoize-one';
import {
  ALERT_PERMISSIONS_MAPPING,
  type AlertPermissionsMapping,
  EAlertTypes,
} from '../../models/alerts/alert-configuration';
import type VesselTypes from '../../models/vessels/vessel-types.model';
import type { PermissionToggles } from '../../settings/admin.slice';
import store from '../../store';
import TenantId from '../../tenant';
import type { Token, UserFeatureFlags } from '../../user/user.slice';
import { UserRole } from '../../utils/user.enum';
import { getReleaseToggles, type ReleaseToggles } from './release-toggles';

export type Privileges = {
  canAccessAlerts: Record<EAlertTypes, boolean>;
  canAccessVesselTypes: Record<VesselTypes, boolean>;
  canAccessIncidents: boolean;
  canAccessMyFleetV2: boolean;
  canAccessFleetMidMismatch: boolean;
  canAccessFleetStats: boolean;
  canAccessPorts: {
    wpi: boolean;
    land: boolean;
    coastal: boolean;
    cruise: boolean;
  };
  canAccessRoutes: boolean;
  canAccessHistory: boolean;
  canAccessVesselHistoryFilters: boolean;
  canAccessBoundaries: boolean;
  canAccessDrawings: boolean;
  canAccessMaps: {
    openSeaMaps: boolean;
    bathymetry: boolean;
    shippingTrafficV2: boolean;
  };
  canAccessSanctions: boolean;
  canAccessRiMaritimeAreas: boolean;
  canAccessTimeline: boolean;
  canAccessMapTools: boolean;
  canAccessGraphs: boolean;
  canAccessNearbyVessels: boolean;
  canAccessHistoricVessels: boolean;
  canAccessWeather: boolean;
  canAccessSharedDrawings: boolean;
  canMultiSelect: boolean;
  canDrawPoints: boolean;
  canAccessAvcsCharts: boolean;
  canAccessFathom: boolean;
  canAccessEsa: boolean;
  canAccessGlobalAis: boolean;
  canAccessMergeStationaryPoints: boolean;
  canAccessCapella: boolean;
  canAccessPermissionToggles: boolean;
  canEditCompanySeats: boolean;
  canAccessMeasuringTool: boolean;
  canAccessRangeRings: boolean;
  canAccessNewsTicker: boolean;
  canAccessRICitiesCountriesData: boolean;
  canAccessPortStats: boolean;
  canAccessMaritimeIncidents: boolean;
};

const getCanAccessPermissionToggles = (idToken: Token) => {
  // Can't import the existing isAdmin function from `user.utils.tsx`, as it would create a circular dependency.
  const isAdminUser = [UserRole.GLOBAL_ADMIN, UserRole.COMPANY_ADMIN].some(
    (role) => store.getState().user.idToken?.groups?.includes(role)
  );

  const canAccessPermissionToggles =
    idToken?.tenantId === TenantId.GEOLLECT && isAdminUser;

  return canAccessPermissionToggles;
};

export const NO_PRIVILEGES: Privileges = {
  canAccessAlerts: {
    AIS_OFF: false,
    AIS_ON: false,
    ENTER_PORT: false,
    ENTER_ROI: false,
    EXIT_ROI: false,
    INCIDENTS: false,
    INCIDENTS_ROUTE: false,
    DEP_PORT: false,
    STS: false,
    STATIC_CHANGE: false,
  },
  canAccessVesselTypes: {
    DRY_BULK: false,
    GENERAL_CARGO: false,
    CONTAINER: false,
    REEFER: false,
    ROLL_ON_ROLL_OFF: false,
    OFFSHORE: false,
    HIGH_SPEED_CRAFT: false,
    CAR_CARRIER: false,
    VEHICLE_PASSENGER: false,
    PLEASURE_CRAFT: false,
    FISHING: false,
    GENERAL_TANKER: false,
    GAS_TANKER: false,
    TANKER_PRODUCT: false,
    PASSENGER: false,
    OTHER_UNKNOWN: false,
    BUOY: false,
    NET: false,
  },
  canAccessIncidents: false,
  canAccessMyFleetV2: false,
  canAccessFleetMidMismatch: false,
  canAccessFleetStats: false,
  canAccessPorts: {
    wpi: false,
    land: false,
    coastal: false,
    cruise: false,
  },
  canAccessRoutes: false,
  canAccessHistory: false,
  canAccessBoundaries: false,
  canAccessDrawings: false,
  canAccessVesselHistoryFilters: false,
  canAccessMaps: {
    openSeaMaps: false,
    bathymetry: false,
    shippingTrafficV2: false,
  },
  canAccessSanctions: false,
  canAccessRiMaritimeAreas: false,
  canAccessTimeline: false,
  canAccessMapTools: false,
  canAccessGraphs: false,
  canAccessNearbyVessels: false,
  canAccessHistoricVessels: false,
  canAccessWeather: false,
  canAccessSharedDrawings: false,
  canMultiSelect: false,
  canDrawPoints: false,
  canAccessAvcsCharts: false,
  canAccessFathom: false,
  canAccessEsa: false,
  canAccessGlobalAis: false,
  canAccessMergeStationaryPoints: false,
  canAccessCapella: false,
  canAccessPermissionToggles: false,
  canEditCompanySeats: false,
  canAccessMeasuringTool: false,
  canAccessRangeRings: false,
  canAccessNewsTicker: false,
  canAccessRICitiesCountriesData: false,
  canAccessPortStats: false,
  canAccessMaritimeIncidents: false,
};

const DEPRECATED_ALERT_TYPES: EAlertTypes[] = [
  EAlertTypes.INCIDENTS,
  EAlertTypes.INCIDENTS_ROUTE,
];

const getCanAccessAlerts = (companyPermissionToggles: PermissionToggles) =>
  Object.keys(ALERT_PERMISSIONS_MAPPING).reduce(
    (acc, alertType) => {
      if (DEPRECATED_ALERT_TYPES.includes(alertType as EAlertTypes)) {
        return { ...acc, [alertType as EAlertTypes]: false };
      }

      const alertPermissions =
        ALERT_PERMISSIONS_MAPPING[alertType as keyof AlertPermissionsMapping];

      const isToggleEnabled = (toggle: keyof PermissionToggles) =>
        companyPermissionToggles[toggle]?.enabled ?? false;

      const allRequiredEnabled = alertPermissions.allOf.every(isToggleEnabled);
      const atLeastOneRequiredEnabled =
        alertPermissions.oneOf.length === 0 ||
        alertPermissions.oneOf.some(isToggleEnabled);

      return {
        ...acc,
        [alertType as EAlertTypes]:
          allRequiredEnabled && atLeastOneRequiredEnabled,
      };
    },
    {} as Record<EAlertTypes, boolean>
  );

type GeollectPermissionTogglePrivilegesArgs = {
  releaseToggles: ReleaseToggles;
  permissionToggles: PermissionToggles | null;
  idToken: Token;
};

export const geollectPrivilegesPermissionsTogglesV1 = memoizeOne(
  ({
    permissionToggles,
    releaseToggles,
    idToken,
  }: GeollectPermissionTogglePrivilegesArgs): Privileges =>
    permissionToggles
      ? {
          canAccessAvcsCharts: !!permissionToggles.avcs?.enabled,
          canAccessGlobalAis: !!permissionToggles.global_ais?.enabled,
          canAccessRoutes: !!permissionToggles.routes?.enabled,
          canAccessMaps: {
            openSeaMaps: !!permissionToggles.opensea_maps?.enabled,
            bathymetry: !!permissionToggles.bathymetry?.enabled,
            shippingTrafficV2: !!permissionToggles.shipping_lanes?.enabled,
          },
          canAccessPorts: {
            wpi: !!permissionToggles.wpi_ports?.enabled,
            land: !!permissionToggles.ri_ports?.enabled,
            coastal: !!permissionToggles.ri_ports?.enabled,
            cruise: !!permissionToggles.ri_ports?.enabled,
          },
          canAccessVesselTypes: Object.keys(
            NO_PRIVILEGES.canAccessVesselTypes
          ).reduce(
            (acc, vesselType) => ({
              ...acc,
              [vesselType]:
                !!permissionToggles.fleets?.enabled ||
                !!permissionToggles.nearby_vessel?.enabled,
            }),
            NO_PRIVILEGES.canAccessVesselTypes
          ),
          canAccessCapella: !!permissionToggles.sar_imagery?.enabled,
          canAccessRiMaritimeAreas: !!permissionToggles.ri_areas?.enabled,
          canAccessBoundaries: !!permissionToggles.boundaries?.enabled,
          canAccessEsa: !!permissionToggles.esa_maps?.enabled,
          canAccessIncidents: !!permissionToggles.ri_incidents?.enabled,
          canAccessDrawings: !!permissionToggles.drawings?.enabled,
          canAccessSharedDrawings: !!permissionToggles.drawings?.enabled,
          canAccessPermissionToggles: getCanAccessPermissionToggles(idToken),
          canAccessHistory:
            !!permissionToggles.history_specific_period?.enabled,
          canAccessVesselHistoryFilters:
            !!permissionToggles.history_specific_period?.enabled,
          canEditCompanySeats: true,
          canAccessMeasuringTool: !!permissionToggles.measuring_tool?.enabled,
          canAccessMapTools:
            !!permissionToggles.measuring_tool?.enabled ||
            !!permissionToggles.range_rings?.enabled ||
            !!permissionToggles.weather?.enabled ||
            !!permissionToggles.nearby_vessel?.enabled ||
            !!permissionToggles.search_by_area?.enabled, // Add || when other toggles for Tools are implemented
          canAccessAlerts: getCanAccessAlerts(permissionToggles),
          canAccessRangeRings: !!permissionToggles.range_rings?.enabled,
          canAccessWeather: !!permissionToggles.weather?.enabled,
          canAccessNearbyVessels: !!permissionToggles.nearby_vessel?.enabled,
          canAccessHistoricVessels: !!permissionToggles.search_by_area?.enabled,
          canAccessMyFleetV2: !!permissionToggles.fleets?.enabled,
          canAccessFleetMidMismatch:
            !!permissionToggles.fleet_mid_mismatch?.enabled,
          canAccessFleetStats: !!permissionToggles.fleet_stats?.enabled,
          canAccessRICitiesCountriesData:
            !!permissionToggles.ri_cities_countries_data?.enabled,
          canAccessNewsTicker:
            !!permissionToggles.ri_incidents?.enabled ||
            !!permissionToggles.ri_cities_countries_data?.enabled ||
            !!permissionToggles.ri_areas?.enabled ||
            !!permissionToggles.ri_ports?.enabled,
          canAccessSanctions:
            !!permissionToggles.sanctions_countries_data?.enabled,
          canAccessTimeline:
            !!permissionToggles.history_specific_period?.enabled,
          canAccessGraphs: true,
          canMultiSelect: true,
          canDrawPoints: !!permissionToggles.drawings?.enabled,
          canAccessFathom: true,
          canAccessMergeStationaryPoints: true,
          canAccessPortStats: !!permissionToggles.port_stats?.enabled,
          canAccessMaritimeIncidents:
            !!permissionToggles.maritime_incidents?.enabled,
        }
      : NO_PRIVILEGES
);

type CalculatePrivilegesArgs = {
  idToken: Token | null;
  userFeatureFlags: UserFeatureFlags | null;
  permissionToggles: PermissionToggles | null;
};

export const calculatePrivileges = ({
  idToken,
  permissionToggles,
}: CalculatePrivilegesArgs) => {
  if (!idToken?.tenantId) return NO_PRIVILEGES;
  const releaseToggles = getReleaseToggles();

  const privileges: Record<TenantId, Privileges> = {
    [TenantId.GEOLLECT]: geollectPrivilegesPermissionsTogglesV1({
      permissionToggles,
      releaseToggles,
      idToken,
    }),
  };

  return privileges[idToken.tenantId];
};
